import type { Patch } from "./types";

/** A warm pulse-wave lead with a gentle filter and slow pitch vibrato. */
export const defaultPatch: Patch = {
  version: 1,
  name: "Init Lead",
  oscillator: {
    type: "pulse",
    detune: 0,
    pulseWidth: 0.5,
  },
  filter: {
    type: "lowpass",
    cutoff: 2000,
    q: 6,
  },
  ampEnv: {
    attack: 0.01,
    decay: 0.2,
    sustain: 0.7,
    release: 0.4,
  },
  lfo: {
    target: "pitch",
    shape: "sine",
    rate: 5,
    depth: 8,
  },
  masterGain: 0.3,
};

/** Deep-clone a patch so editing one instance never mutates another (e.g. presets). */
export function clonePatch(patch: Patch): Patch {
  return structuredClone(patch);
}

// Build a valid Patch from untrusted JSON by overlaying it on the defaults, so
// missing or malformed fields fall back instead of breaking the engine.
export function coercePatch(raw: unknown): Patch {
  const base = clonePatch(defaultPatch);
  if (typeof raw !== "object" || raw === null) return base;
  const r = raw as Record<string, unknown>;
  const merge = <T extends object>(target: T, src: unknown): T => {
    if (typeof src === "object" && src !== null) Object.assign(target, src);
    return target;
  };
  if (typeof r.name === "string") base.name = r.name;
  merge(base.oscillator, r.oscillator);
  merge(base.filter, r.filter);
  merge(base.ampEnv, r.ampEnv);
  merge(base.lfo, r.lfo);
  if (typeof r.masterGain === "number") base.masterGain = r.masterGain;
  if (typeof r.drive === "number") base.drive = Math.max(0, Math.min(1, r.drive));
  if (r.pitchEnv && typeof r.pitchEnv === "object") {
    const pe = r.pitchEnv as Record<string, unknown>;
    base.pitchEnv = {
      amount: typeof pe.amount === "number" ? pe.amount : 0,
      decay: typeof pe.decay === "number" ? pe.decay : 0.08,
    };
  }
  if (r.fm && typeof r.fm === "object") {
    const fm = r.fm as Record<string, unknown>;
    base.fm = {
      ratio: typeof fm.ratio === "number" ? fm.ratio : 1,
      depth: typeof fm.depth === "number" ? fm.depth : 0,
    };
  }
  if (r.filterEnv && typeof r.filterEnv === "object") {
    const fe = r.filterEnv as Record<string, unknown>;
    base.filterEnv = {
      amount: typeof fe.amount === "number" ? fe.amount : 0,
      decay: typeof fe.decay === "number" ? fe.decay : 0.15,
    };
  }
  if (r.xfadeTo && typeof r.xfadeTo === "object") base.xfadeTo = coercePatch(r.xfadeTo);
  return base;
}
